import axios from "axios";
import { config, config_multipart } from "../../api-helper/api-header-configs";
import { Asset, UploadProgress, DownloadRequest, AssetFilter, AssetCategory, AssetMetadata } from "../types/assetTypes";

const baseURL = process.env.REACT_APP_API_URL;

// Initialize multipart upload (for large files)
export const init_multipart_upload = async (file_name: string, file_size: number, file_type: string) => {
  const response = await axios.post(
    baseURL + "/assets/init-upload",
    { file_name, file_size, file_type },
    config
  );
  return response.data;
}

// Upload chunk
export const upload_chunk = async (
  upload_id: string,
  chunk_index: number,
  chunk_data: Blob,
  file_name: string
) => {
  const formData = new FormData();
  formData.append('upload_id', upload_id);
  formData.append('chunk_index', chunk_index.toString());
  formData.append('chunk', chunk_data);
  formData.append('file_name', file_name);

  const response = await axios.post(
    baseURL + "/assets/upload-chunk",
    formData,
    config_multipart
  );
  return response.data;
}

// Complete multipart upload
export const complete_multipart_upload = async (upload_id: string, asset_data: Partial<Asset>) => {
  const response = await axios.post(
    baseURL + "/assets/complete-upload",
    { upload_id, ...asset_data },
    config
  );
  return response.data;
}

// Upload asset (for smaller files or direct upload)
export const upload_asset = async (formData: FormData, onUploadProgress?: (progress: number) => void) => {
  const response = await axios.post(
    baseURL + "/assets/upload",
    formData,
    {
      ...config_multipart,
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percentCompleted);
        }
      }
    }
  );
  return response.data;
}

// Get filtered assets - POST method to /api/filtered_uploads
export const get_filtered_assets = async (filters?: {
  upload_name?: string;
  category_flag?: 'specific' | 'all';
  category_id?: number;
  platform_flag?: 'specific' | 'all';
  platform_id?: number;
  page?: number;
  per_page?: number;
}) => {
  try {
    const payload: any = {};
    
    if (filters?.upload_name) {
      payload.upload_name = filters.upload_name;
    }
    
    if (filters?.category_flag) {
      payload.category_flag = filters.category_flag;
      if (filters.category_flag === 'specific' && filters.category_id) {
        payload.category_id = filters.category_id;
      }
    } else {
      payload.category_flag = 'all';
    }
    
    if (filters?.platform_flag) {
      payload.platform_flag = filters.platform_flag;
      if (filters.platform_flag === 'specific' && filters.platform_id) {
        payload.platform_id = filters.platform_id;
      }
    } else {
      payload.platform_flag = 'all';
    }

    if (filters?.page) {
      payload.page = filters.page;
    }

    if (filters?.per_page) {
      payload.per_page = filters.per_page;
    }

    const response = await axios.post(
      baseURL + "/filtered_uploads",
      payload,
      config
    );

    // Map API response to Asset format
    // Response structure: data.uploads.data (paginated)
    const apiData = response?.data?.data || response?.data || {};
    const uploadsData = apiData.uploads || {};
    const uploads = uploadsData.data || apiData.all_upload || apiData.all_uploads || apiData.uploads || apiData.data || [];
    
    // Helper function to map category name to AssetCategory enum
    const mapCategoryNameToEnum = (categoryName: string): AssetCategory => {
      const name = categoryName.toLowerCase().trim();
      if (name.includes('video template') || name === 'video_templates') {
        return AssetCategory.VIDEO_TEMPLATES;
      } else if (name.includes('stock video') || name === 'stock_videos') {
        return AssetCategory.STOCK_VIDEOS;
      } else if (name.includes('audio') || name === 'audios') {
        return AssetCategory.AUDIOS;
      } else if (name.includes('graphic') || name === 'graphics') {
        return AssetCategory.GRAPHICS;
      }
      return AssetCategory.GRAPHICS; // default
    };
    
    const assets: Asset[] = uploads.map((upload: any) => {
      // Helper function to construct full URL from file path
      const getThumbnailUrl = (filePath: string | null | undefined): string | null => {
        if (!filePath) return null;
        
        // If already a full URL, return as is
        if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
          return filePath;
        }
        
        // Construct full URL from base URL
        let apiBaseUrl = baseURL || process.env.REACT_APP_API_URL || '';
        // Remove /api suffix if present, but keep the base domain
        apiBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
        // Ensure base URL doesn't end with slash
        apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
        
        // Handle file path - remove leading slash if present, then add it back
        const cleanPath = filePath.replace(/^\//, '');
        
        return `${apiBaseUrl}/${cleanPath}`;
      };

      // Get primary thumbnail from thumbnails array or primary_thumbnail_file_path
      let primaryThumbnailUrl: string | null = null;
      
      // First check if primary_thumbnail_file_path exists (direct field)
      if (upload.primary_thumbnail_file_path) {
        primaryThumbnailUrl = getThumbnailUrl(upload.primary_thumbnail_file_path);
      } else if (upload.primary_thumbnail?.file_path) {
        // Check primary_thumbnail object
        primaryThumbnailUrl = getThumbnailUrl(upload.primary_thumbnail.file_path);
      } else {
        // Otherwise, check thumbnails array
        const thumbnailsArray = upload.thumbnails || [];
        
        if (thumbnailsArray.length > 0) {
          // Find primary thumbnail (is_primary === 1 or is_primary === true)
          const primaryThumbnail = thumbnailsArray.find((thumb: any) => 
            thumb.is_primary === 1 || thumb.is_primary === true || thumb.is_primary === '1'
          );
          
          if (primaryThumbnail) {
            const filePath = primaryThumbnail.file_path 
              || primaryThumbnail.image_url 
              || primaryThumbnail.url 
              || primaryThumbnail.thumbnail_url;
            primaryThumbnailUrl = getThumbnailUrl(filePath);
          } else {
            // If no primary found, use first thumbnail
            const firstThumb = thumbnailsArray[0];
            const filePath = firstThumb.file_path 
              || firstThumb.image_url 
              || firstThumb.url 
              || firstThumb.thumbnail_url;
            primaryThumbnailUrl = getThumbnailUrl(filePath);
          }
        }
      }

      // Build thumbnails array - use primary thumbnail if available
      const thumbnails: string[] = primaryThumbnailUrl ? [primaryThumbnailUrl] : [];

      // Map category from category.category_name (e.g., "Stock Videos" -> STOCK_VIDEOS)
      const categoryName = upload.category?.category_name || '';
      const category = categoryName ? mapCategoryNameToEnum(categoryName) : AssetCategory.GRAPHICS;

      // Map tags (array of tag objects with tag_title, or empty array)
      const tagsArray = upload.tags || [];
      const mappedTags = tagsArray.map((tag: any) => {
        if (typeof tag === 'string') return tag;
        return tag.tag_title || tag.title || tag.name || '';
      }).filter(Boolean);

      return {
        id: upload.id,
        name: upload.upload_name || upload.name || '',
        category: category,
        category_name: categoryName,
        category_object: upload.category,
        description: upload.description || '',
        tags: mappedTags,
        thumbnails: thumbnails,
        zip_file_url: upload.asset_file_path || upload.file_path || upload.zip_file_url || null,
        licenses: upload.licenses || [],
        metadata: {
          size: upload.file_size || upload.size || undefined,
          file_type: 'zip',
          mime_type: upload.mime_type || 'application/zip',
        } as AssetMetadata,
        source_platform: upload.platform?.platform_name || upload.platform as any,
        platform_object: upload.platform,
        source_url: upload.source_url || '',
        uploaded_at: upload.created_at || upload.uploaded_at || '',
        uploaded_by: upload.updated_by || upload.user_id,
        download_count: upload.download_count || undefined,
        status: upload.status === 1 || upload.status === 'active' ? 'completed' : 'completed',
        created_at: upload.created_at,
        updated_at: upload.updated_at,
      };
    });

    // Extract pagination from uploads object
    const pagination: {
      current_page: number;
      per_page: number;
      total: number;
      total_pages: number;
      from: number;
      to: number;
      last_page: number;
      first_page_url: string;
      last_page_url: string;
      next_page_url: string | null;
      prev_page_url: string | null;
      path: string;
      links: any[];
    } = uploadsData.current_page !== undefined ? {
      current_page: uploadsData.current_page || 1,
      per_page: uploadsData.per_page || 10,
      total: uploadsData.total || assets.length,
      total_pages: uploadsData.last_page || Math.ceil((uploadsData.total || assets.length) / (uploadsData.per_page || 10)),
      from: uploadsData.from || 0,
      to: uploadsData.to || assets.length,
      last_page: uploadsData.last_page || 1,
      first_page_url: uploadsData.first_page_url || '',
      last_page_url: uploadsData.last_page_url || '',
      next_page_url: uploadsData.next_page_url || null,
      prev_page_url: uploadsData.prev_page_url || null,
      path: uploadsData.path || '',
      links: uploadsData.links || [],
    } : {
      current_page: 1,
      per_page: 12,
      total: assets.length,
      total_pages: Math.ceil(assets.length / 12),
      from: 0,
      to: assets.length,
      last_page: 1,
      first_page_url: '',
      last_page_url: '',
      next_page_url: null,
      prev_page_url: null,
      path: '',
      links: [],
    };

    return {
      assets: assets,
      pagination: pagination,
    };
  } catch (error: any) {
    console.error('Failed to fetch filtered assets:', error);
    return {
      assets: [],
      pagination: {
        current_page: 1,
        per_page: 12,
        total: 0,
        total_pages: 0,
      },
    };
  }
}

// Get all assets - fetch from Laravel API /api/uploads
export const get_all_assets = async (filters?: AssetFilter) => {
  try {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.tags) filters.tags.forEach(tag => params.append('tags[]', tag));
    if (filters?.search) params.append('search', filters.search);
    if (filters?.source_platform) params.append('source_platform', filters.source_platform);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    const response = await axios.get(
      baseURL + "/uploads" + (params.toString() ? `?${params.toString()}` : ''),
      config
    );

    // Map API response to Asset format
    const apiData = response?.data?.data || response?.data || {};
    const uploads = apiData.all_upload || apiData.all_uploads || apiData.uploads || apiData.data || [];
    
    // Helper function to map category name to AssetCategory enum
    const mapCategoryNameToEnum = (categoryName: string): AssetCategory => {
      const name = categoryName.toLowerCase().trim();
      if (name.includes('video template') || name === 'video_templates') {
        return AssetCategory.VIDEO_TEMPLATES;
      } else if (name.includes('stock video') || name === 'stock_videos') {
        return AssetCategory.STOCK_VIDEOS;
      } else if (name.includes('audio') || name === 'audios') {
        return AssetCategory.AUDIOS;
      } else if (name.includes('graphic') || name === 'graphics') {
        return AssetCategory.GRAPHICS;
      }
      return AssetCategory.GRAPHICS; // default
    };
    
    const assets: Asset[] = uploads.map((upload: any) => {
      // Helper function to construct full URL from file path
      const getThumbnailUrl = (filePath: string | null | undefined): string | null => {
        if (!filePath) return null;
        
        // If already a full URL, return as is
        if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
          return filePath;
        }
        
        // Construct full URL from base URL
        let apiBaseUrl = baseURL || process.env.REACT_APP_API_URL || '';
        // Remove /api suffix if present, but keep the base domain
        apiBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
        // Ensure base URL doesn't end with slash
        apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
        
        // Handle file path - remove leading slash if present, then add it back
        const cleanPath = filePath.replace(/^\//, '');
        
        return `${apiBaseUrl}/${cleanPath}`;
      };

      // Get primary thumbnail from thumbnails array or primary_thumbnail_file_path
      let primaryThumbnailUrl: string | null = null;
      
      // First check if primary_thumbnail_file_path exists (direct field)
      if (upload.primary_thumbnail_file_path) {
        primaryThumbnailUrl = getThumbnailUrl(upload.primary_thumbnail_file_path);
      } else if (upload.primary_thumbnail?.file_path) {
        // Check primary_thumbnail object
        primaryThumbnailUrl = getThumbnailUrl(upload.primary_thumbnail.file_path);
      } else {
        // Otherwise, check thumbnails array
        const thumbnailsArray = upload.thumbnails || [];
        
        if (thumbnailsArray.length > 0) {
          // Find primary thumbnail (is_primary === 1 or is_primary === true)
          const primaryThumbnail = thumbnailsArray.find((thumb: any) => 
            thumb.is_primary === 1 || thumb.is_primary === true || thumb.is_primary === '1'
          );
          
          if (primaryThumbnail) {
            const filePath = primaryThumbnail.file_path 
              || primaryThumbnail.image_url 
              || primaryThumbnail.url 
              || primaryThumbnail.thumbnail_url;
            primaryThumbnailUrl = getThumbnailUrl(filePath);
          } else {
            // If no primary found, use first thumbnail
            const firstThumb = thumbnailsArray[0];
            const filePath = firstThumb.file_path 
              || firstThumb.image_url 
              || firstThumb.url 
              || firstThumb.thumbnail_url;
            primaryThumbnailUrl = getThumbnailUrl(filePath);
          }
        }
      }

      // Build thumbnails array - use primary thumbnail if available
      const thumbnails: string[] = primaryThumbnailUrl ? [primaryThumbnailUrl] : [];

      // Map category from category.category_name (e.g., "Stock Videos" -> STOCK_VIDEOS)
      const categoryName = upload.category?.category_name || '';
      const category = categoryName ? mapCategoryNameToEnum(categoryName) : AssetCategory.GRAPHICS;

      // Map tags (array of tag objects with tag_title, or empty array)
      const tagsArray = upload.tags || [];
      const mappedTags = tagsArray.map((tag: any) => {
        if (typeof tag === 'string') return tag;
        return tag.tag_title || tag.title || tag.name || '';
      }).filter(Boolean);

      return {
        id: upload.id,
        name: upload.upload_name || upload.name || '',
        category: category,
        category_name: categoryName,
        category_object: upload.category,
        description: upload.description || '',
        tags: mappedTags,
        thumbnails: thumbnails,
        zip_file_url: upload.asset_file_path || upload.file_path || upload.zip_file_url || null,
        licenses: upload.licenses || [],
        metadata: {
          size: upload.file_size || upload.size || undefined,
          file_type: 'zip',
          mime_type: upload.mime_type || 'application/zip',
        } as AssetMetadata,
        source_platform: upload.platform?.platform_name || upload.platform as any,
        platform_object: upload.platform,
        source_url: upload.source_url || '',
        uploaded_at: upload.created_at || upload.uploaded_at || '',
        uploaded_by: upload.updated_by || upload.user_id,
        download_count: upload.download_count || undefined,
        status: upload.status === 1 || upload.status === 'active' ? 'completed' : 'completed',
        created_at: upload.created_at,
        updated_at: upload.updated_at,
      };
    });

    // Return with pagination if available from API
    const pagination = apiData.pagination || {
      current_page: filters?.page || 1,
      per_page: filters?.per_page || 12,
      total: assets.length,
      total_pages: Math.ceil(assets.length / (filters?.per_page || 12)),
    };

    return {
      assets: assets,
      pagination: pagination,
    };
  } catch (error: any) {
    console.error('Failed to fetch assets:', error);
    return {
      assets: [],
      pagination: {
        current_page: 1,
        per_page: 12,
        total: 0,
        total_pages: 0,
      },
    };
  }
}

// Get single asset/upload by id
export const get_single_asset = async (id: number) => {
  const response = await axios.get(baseURL + `/uploads/${id}`, config);
  return response.data;
}

// Update asset
export const update_asset = async (id: number, asset_data: Partial<Asset>) => {
  const response = await axios.put(baseURL + `/assets/${id}`, asset_data, config);
  return response.data;
}

// Delete asset
export const delete_asset = async (id: number) => {
  const response = await axios.delete(baseURL + `/uploads/${id}`, config);
  return response.data;
}

// Download asset
export const download_asset = async (download_request: DownloadRequest) => {
  const response = await axios.post(
    baseURL + "/assets/download",
    download_request,
    {
      ...config,
      responseType: 'blob'
    }
  );
  return response.data;
}

// Get download URL (presigned URL from Wasabi)
export const get_download_url = async (asset_id: number) => {
  const response = await axios.get(baseURL + `/assets/${asset_id}/download-url`, config);
  return response.data;
}

// Get upload status
export const get_upload_status = async (upload_id: string) => {
  const response = await axios.get(baseURL + `/assets/upload-status/${upload_id}`, config);
  return response.data;
}

// Get all workspaces (for download selection)
export const get_all_workspaces = async () => {
  const response = await axios.get(baseURL + "/workspaces", config);
  return response.data;
}

// Get projects by workspace
export const get_projects_by_workspace = async (workspace_id: number) => {
  const response = await axios.get(baseURL + `/workspaces/${workspace_id}/projects`, config);
  return response.data;
}

// Upload asset to server
export const upload_asset_to_server = async (formData: FormData, onUploadProgress?: (progress: number) => void) => {
  const response = await axios.post(
    baseURL + "/uploads",
    formData,
    {
      ...config_multipart,
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percentCompleted);
        }
      }
    }
  );
  return response.data;
}

// Update upload metadata (for edit mode)
export const update_upload_metadata = async (uploadId: string, formData: FormData, onUploadProgress?: (progress: number) => void) => {
  try {
    const response = await axios.put(
      baseURL + `/uploads/${uploadId}`,
      formData,
      {
        ...config_multipart,
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onUploadProgress(percentCompleted);
          }
        }
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('Update upload metadata error:', error);
    console.error('Error response:', error?.response?.data);
    console.error('Request URL:', baseURL + `/uploads/${uploadId}`);
    throw error;
  }
}

// Upload response API (called when Wasabi upload completes)
export const upload_response = async (uploadId: string, assetFilePath: string) => {
  const payload = {
    upload_id: uploadId,
    asset_file_path: assetFilePath
  };
  const response = await axios.post(baseURL + "/upload_response", payload, config);
  return response.data;
}

// Update license API
export const update_license = async (formData: FormData) => {
  const response = await axios.post(baseURL + "/update_license", formData, config_multipart);
  return response.data;
}